import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  FileText, CheckCircle2, RefreshCw, MessageSquare, User,
  ChevronDown, ChevronRight, Download, Eye 
} from 'lucide-react';
import { BRAND_COLORS, BRAND_LABELS, API, BACKEND_URL } from '@/constants/bluemonk';

export const ApprovalsTab = ({ items, onApprove, onRequestRevision, onComment, actor, headers }) => {
  const [showApproved, setShowApproved] = useState(false);
  const [showAwaitingRevision, setShowAwaitingRevision] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  const pendingReviewItems = items.filter(d => 
    ['submitted-for-review', 'revision-submitted'].includes(d.status)
  );
  const awaitingRevisionItems = items.filter(d => d.status === 'revision-requested');
  const approvedItems = items.filter(d => d.status === 'approved');

  const calculateDaysRemaining = (deadline) => {
    if (!deadline) return null;
    const today = new Date();
    const due = new Date(deadline);
    const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const handleAddComment = async (itemId) => {
    if (!commentText.trim() || !actor) return;
    setSubmittingComment(true);
    try {
      await axios.post(`${API}/deliverables/${itemId}/comments`, {
        actor,
        content: commentText,
        authorType: 'client'
      }, { headers });
      toast.success('Comment added');
      setCommentText('');
      onComment?.();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const isImageFile = (fileName) => /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
  const isPdfFile = (fileName) => /\.pdf$/i.test(fileName);

  if (pendingReviewItems.length === 0 && awaitingRevisionItems.length === 0 && approvedItems.length === 0) {
    return (
      <Card className="p-8 text-center">
        <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <p className="text-lg font-medium text-slate-700">No deliverables yet</p>
        <p className="text-slate-500">Deliverables will appear here when submitted for review</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pending Reviews */}
      {pendingReviewItems.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900">Pending Your Review ({pendingReviewItems.length})</h3>
          {pendingReviewItems.map((item) => {
            const colors = BRAND_COLORS[item.brand];
            const daysRemaining = calculateDaysRemaining(item.reviewDeadline);
            const isOverdue = daysRemaining !== null && daysRemaining < 0;
            const isExpanded = expandedItem === item.id;

            return (
              <Card 
                key={item.id} 
                className={`border-l-4 ${colors.border} ${isOverdue ? 'bg-red-50/50' : ''}`}
                data-testid={`deliverable-card-${item.id}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-slate-900 text-lg">{item.title}</h4>
                        <Badge className={`${colors.badge} text-white text-xs`}>
                          {BRAND_LABELS[item.brand]}
                        </Badge>
                        {item.status === 'revision-submitted' && (
                          <Badge className="bg-purple-500 text-white">Revision Submitted</Badge>
                        )}
                        {isOverdue && <Badge variant="destructive">Review Overdue</Badge>}
                      </div>

                      {item.description && (
                        <p className="text-sm text-slate-600 mb-3">{item.description}</p>
                      )}

                      <div className="flex items-center gap-6 text-sm text-slate-600">
                        <div>
                          <span className="font-medium">Submitted:</span>{' '}
                          {item.submittedAt && new Date(item.submittedAt).toLocaleDateString()}
                          {item.submittedBy && <span className="text-slate-400"> by {item.submittedBy.name}</span>}
                        </div>
                        <div>
                          <span className="font-medium">Review by:</span>{' '}
                          <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>
                            {item.reviewDeadline && new Date(item.reviewDeadline).toLocaleDateString()}
                            {daysRemaining !== null && (
                              <span className="ml-1">
                                ({daysRemaining > 0 ? `${daysRemaining} days` : daysRemaining === 0 ? 'Today' : 'Overdue'})
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        onClick={() => onApprove(item)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-btn-${item.id}`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => onRequestRevision(item)}
                        data-testid={`revision-btn-${item.id}`}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Request Revision
                      </Button>
                    </div>
                  </div>

                  {/* Files Section */}
                  {item.files?.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium text-slate-700 mb-3">Attachments ({item.files.length})</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {item.files.map((file) => (
                          <a
                            key={file.id}
                            href={`${BACKEND_URL}${file.fileUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative flex flex-col items-center p-4 bg-slate-50 rounded-lg border hover:border-primary hover:bg-primary/5 transition-colors"
                          >
                            {isImageFile(file.fileName) ? (
                              <div className="w-full h-24 mb-2 overflow-hidden rounded">
                                <img 
                                  src={`${BACKEND_URL}${file.fileUrl}`} 
                                  alt={file.fileName}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : isPdfFile(file.fileName) ? (
                              <div className="w-full h-24 mb-2 flex items-center justify-center bg-red-50 rounded">
                                <FileText className="h-12 w-12 text-red-500" />
                              </div>
                            ) : (
                              <div className="w-full h-24 mb-2 flex items-center justify-center bg-slate-100 rounded">
                                <FileText className="h-12 w-12 text-slate-400" />
                              </div>
                            )}
                            <span className="text-xs text-slate-600 truncate max-w-full">{file.fileName}</span>
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                              <div className="flex items-center gap-2 text-white text-sm">
                                <Eye className="h-4 w-4" />
                                View
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Toggle Details */}
                  <button
                    onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                    className="mt-4 text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {isExpanded ? 'Hide' : 'Show'} comments & history
                    {(item.comments?.length > 0 || item.revisionRequests?.length > 0) && (
                      <span className="text-slate-400 ml-1">
                        ({(item.comments?.length || 0) + (item.revisionRequests?.length || 0)})
                      </span>
                    )}
                  </button>
                </div>

                {/* Expanded Section */}
                {isExpanded && (
                  <div className="px-6 pb-6 space-y-4">
                    {/* Revision History */}
                    {item.revisionRequests?.length > 0 && (
                      <div className="p-4 bg-amber-50 rounded-lg">
                        <p className="text-sm font-medium text-amber-800 mb-2">Revision History</p>
                        <div className="space-y-2">
                          {item.revisionRequests.map((rev) => (
                            <div key={rev.id} className="text-sm">
                              <div className="flex items-center gap-2">
                                <RefreshCw className={`h-4 w-4 ${rev.status === 'open' ? 'text-amber-600' : 'text-green-600'}`} />
                                <span className="font-medium">{rev.requestedBy?.name}</span>
                                <span className="text-slate-500">requested revision</span>
                                <span className="text-xs text-slate-400">
                                  {new Date(rev.requestedAt).toLocaleDateString()}
                                </span>
                                <Badge variant={rev.status === 'open' ? 'outline' : 'default'} className="text-xs">
                                  {rev.status}
                                </Badge>
                              </div>
                              <p className="ml-6 text-slate-600 mt-1">"{rev.reason}"</p>
                              {rev.cvResponse && (
                                <p className="ml-6 text-slate-500 mt-1 italic">
                                  Response: {rev.cvResponse}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Comments */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-slate-700 mb-3">Comments</p>
                      
                      {item.comments?.length > 0 ? (
                        <div className="space-y-3 mb-4">
                          {item.comments.map((comment) => (
                            <div key={comment.id} className="flex gap-3">
                              <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                                <User className="h-4 w-4 text-slate-500" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{comment.author?.name}</span>
                                  <Badge variant={comment.authorType === 'admin' ? 'default' : 'outline'} className="text-xs">
                                    {comment.authorType === 'admin' ? 'Content Vista' : 'Client'}
                                  </Badge>
                                  <span className="text-xs text-slate-400">
                                    {new Date(comment.createdAt).toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-600 mt-1">{comment.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400 mb-4">No comments yet</p>
                      )}

                      {/* Add Comment */}
                      <div className="flex gap-2">
                        <Textarea
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          placeholder="Add a comment..."
                          rows={2}
                          className="flex-1 text-sm"
                        />
                        <Button
                          onClick={() => handleAddComment(item.id)}
                          disabled={!commentText.trim() || submittingComment}
                          size="sm"
                        >
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {isOverdue && (
                  <div className="px-6 pb-4">
                    <p className="text-xs text-slate-500 italic">
                      Delays in approval automatically extend project timelines per our agreement.
                    </p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Awaiting Revision */}
      {awaitingRevisionItems.length > 0 && (
        <div>
          <button
            onClick={() => setShowAwaitingRevision(!showAwaitingRevision)}
            className="flex items-center gap-2 text-lg font-semibold text-amber-700 mb-4"
          >
            {showAwaitingRevision ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            <RefreshCw className="h-5 w-5" />
            Awaiting Revision ({awaitingRevisionItems.length})
          </button>
          
          {showAwaitingRevision && (
            <div className="space-y-3">
              {awaitingRevisionItems.map((item) => {
                const colors = BRAND_COLORS[item.brand];
                const openRevisions = item.revisionRequests?.filter(r => r.status === 'open') || [];
                const isExpanded = expandedItem === item.id;
                
                return (
                  <Card key={item.id} className="border-l-4 border-l-amber-500 bg-amber-50/30">
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <RefreshCw className="h-5 w-5 text-amber-600" />
                          <span className="font-medium">{item.title}</span>
                          <Badge className={`${colors.badge} text-white text-xs`}>
                            {BRAND_LABELS[item.brand]}
                          </Badge>
                          <Badge variant="outline" className="border-amber-500 text-amber-700 text-xs">
                            Revision Requested
                          </Badge>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setExpandedItem(isExpanded ? null : item.id)}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      </div>
                      
                      {openRevisions.length > 0 && (
                        <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                          <p className="text-sm text-slate-600">
                            <span className="font-medium">Your feedback:</span> "{openRevisions[openRevisions.length - 1].reason}"
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            Requested on {new Date(openRevisions[openRevisions.length - 1].requestedAt).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      
                      <p className="text-xs text-amber-600 mt-3">
                        Content Vista is working on your revision. You'll be notified when it's ready for review.
                      </p>
                    </div>
                    
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t">
                        {item.files?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-slate-700 mb-2">Current Files</p>
                            <div className="flex flex-wrap gap-2">
                              {item.files.map((file) => (
                                <a
                                  key={file.id}
                                  href={`${BACKEND_URL}${file.fileUrl}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs hover:bg-slate-200"
                                >
                                  <FileText className="h-3 w-3" />
                                  {file.fileName}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {item.revisionRequests?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-slate-700 mb-2">Revision History</p>
                            <div className="space-y-2">
                              {item.revisionRequests.map((rev) => (
                                <div key={rev.id} className="text-sm p-2 bg-white rounded border">
                                  <div className="flex items-center gap-2">
                                    <Badge variant={rev.status === 'open' ? 'outline' : 'default'} className="text-xs">
                                      {rev.status}
                                    </Badge>
                                    <span className="text-xs text-slate-400">
                                      {new Date(rev.requestedAt).toLocaleString()}
                                    </span>
                                  </div>
                                  <p className="text-slate-600 mt-1">"{rev.reason}"</p>
                                  {rev.cvResponse && (
                                    <p className="text-slate-500 mt-1 italic text-xs">
                                      CV Response: {rev.cvResponse}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {item.comments?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-slate-700 mb-2">Comments</p>
                            <div className="space-y-2">
                              {item.comments.map((comment) => (
                                <div key={comment.id} className="text-sm p-2 bg-white rounded border">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{comment.author?.name}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {comment.authorType === 'admin' ? 'CV' : 'Client'}
                                    </Badge>
                                  </div>
                                  <p className="text-slate-600 mt-1">{comment.content}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-4 flex gap-2">
                          <Textarea
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder="Add a comment..."
                            rows={2}
                            className="flex-1 text-sm"
                          />
                          <Button
                            onClick={() => handleAddComment(item.id)}
                            disabled={!commentText.trim() || submittingComment}
                            size="sm"
                          >
                            <MessageSquare className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Approved Section */}
      {approvedItems.length > 0 && (
        <div>
          <button
            onClick={() => setShowApproved(!showApproved)}
            className="flex items-center gap-2 text-lg font-semibold text-slate-700 mb-4"
          >
            {showApproved ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            Approved ({approvedItems.length})
          </button>
          
          {showApproved && (
            <div className="space-y-3">
              {approvedItems.map((item) => {
                const colors = BRAND_COLORS[item.brand];
                return (
                  <Card key={item.id} className="p-4 border-l-4 border-l-green-500 bg-green-50/50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        <span className="font-medium">{item.title}</span>
                        <Badge className={`${colors.badge} text-white text-xs`}>
                          {BRAND_LABELS[item.brand]}
                        </Badge>
                      </div>
                      {item.approvalRecord && (
                        <span className="text-sm text-slate-600">
                          Approved by {item.approvalRecord.approvedBy?.name} on{' '}
                          {new Date(item.approvalRecord.approvedAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {item.files?.length > 0 && (
                      <div className="mt-3 flex gap-2">
                        {item.files.map((file) => (
                          <a
                            key={file.id}
                            href={`${BACKEND_URL}${file.fileUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline flex items-center gap-1"
                          >
                            <Download className="h-3 w-3" />
                            {file.fileName}
                          </a>
                        ))}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
